import { OrdersActionTypes } from 'redux/types/OrdersTypes';
import { UserActionTypes } from 'redux/types/UserTypes';
import {
  flattenHomeColumns,
  getEmptyHomeColumns,
  removeOrderFromColumns,
  updateOrderInColumns,
  upsertOrderInColumns,
} from 'redux/utils/OrdersUtils';

const INITIAL_STATE = {
  name: 'orders',
  homeList: [],
  homeColumns: getEmptyHomeColumns(),
  homeListIsEmpty: false,
  clientList: [],
  clientListIsEmpty: false,
  isFetching: false,
  isSubmitting: false,
  newOrdersCount: 0,
  order: null,
  productsModal: false,
};

const syncHomeList = (columns) => flattenHomeColumns(columns);

const ordersReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
  case UserActionTypes.USER_LOGOUT_SUCCESS:
    return { ...INITIAL_STATE };

  case OrdersActionTypes.SET_PRODUCTS_MODAL:
    return {
      ...state,
      productsModal: action.payload,
    };

  case OrdersActionTypes.SET_ORDER:
    return {
      ...state,
      order: action.payload,
    };

  case OrdersActionTypes.SET_FETCHING_ORDERS:
    return {
      ...state,
      isFetching: action.payload,
    };

  case OrdersActionTypes.FETCH_ORDER_UPDATE_SUCCESS: {
    const homeColumns = updateOrderInColumns(state.homeColumns, action.payload);

    return {
      ...state,
      order: action.payload,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.UPLOAD_NEW_ORDER: {
    const homeColumns = upsertOrderInColumns(state.homeColumns, action.payload);

    return {
      ...state,
      homeColumns,
      homeList: syncHomeList(homeColumns),
      homeListIsEmpty: false,
    };
  }

  case OrdersActionTypes.UPDATE_ORDER_STATUS: {
    const {
      id,
      status,
    } = action.payload;
    const {
      columns,
      removedOrder,
    } = removeOrderFromColumns(state.homeColumns, id);

    let homeColumns = columns;

    if (removedOrder) {
      homeColumns = upsertOrderInColumns(columns, {
        ...removedOrder,
        status,
      });
    }

    return {
      ...state,
      order: state.order && state.order.id === id
        ? {
          ...state.order,
          status,
        } : state.order,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.FETCH_ORDERS_HOME_SUCCESS:
    return {
      ...state,
      homeList: action.payload,
    };

  case OrdersActionTypes.FETCH_ORDERS_HOME_COLUMNS_SUCCESS: {
    const homeColumns = {
      ...getEmptyHomeColumns(),
      ...action.payload,
    };

    return {
      ...state,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.APPEND_ORDERS_HOME_COLUMN_SUCCESS: {
    const {
      status,
      results = [],
      count,
      hasMore,
    } = action.payload;
    const column = state.homeColumns[status] || {
      results: [],
      count: 0,
      hasMore: false,
      isLoadingMore: false,
    };
    const existingIds = new Set(column.results.map((order) => order.id));
    const uniqueResults = results.filter((order) => !existingIds.has(order.id));
    const homeColumns = {
      ...state.homeColumns,
      [status]: {
        ...column,
        results: [
          ...column.results,
          ...uniqueResults,
        ],
        count: typeof count === 'number' ? count : column.count,
        hasMore,
        isLoadingMore: false,
      },
    };

    return {
      ...state,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.SET_ORDERS_COLUMN_LOADING_MORE: {
    const {
      status,
      value,
    } = action.payload;
    const column = state.homeColumns[status];

    if (!column) {
      return state;
    }

    return {
      ...state,
      homeColumns: {
        ...state.homeColumns,
        [status]: {
          ...column,
          isLoadingMore: value,
        },
      },
    };
  }

  case OrdersActionTypes.SET_ORDERS_HOME_IS_EMPTY:
    return {
      ...state,
      homeListIsEmpty: action.payload,
    };

  case OrdersActionTypes.CLEAR_ORDERS_HOME:
    return {
      ...state,
      homeList: [],
      homeColumns: getEmptyHomeColumns(),
      homeListIsEmpty: false,
    };

  case OrdersActionTypes.FETCH_ORDERS_CLIENT_SUCCESS:
    return {
      ...state,
      clientList: action.payload,
    };

  case OrdersActionTypes.SET_ORDERS_CLIENT_IS_EMPTY:
    return {
      ...state,
      clientListIsEmpty: action.payload,
    };

  case OrdersActionTypes.CLEAR_ORDERS_CLIENT:
    return {
      ...state,
      clientList: [],
      clientListIsEmpty: false,
    };

  case OrdersActionTypes.SET_FETCHING_ORDER_SUBMITTING:
    return {
      ...state,
      isSubmitting: action.payload,
    };

  case OrdersActionTypes.FETCH_ORDER_DELETE_SUCCESS: {
    const { columns: homeColumns } = removeOrderFromColumns(state.homeColumns, action.payload);

    return {
      ...state,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.FETCH_ORDER_TOGGLE_STATUS_SUCCESS: {
    const homeColumns = updateOrderInColumns(state.homeColumns, action.payload);

    return {
      ...state,
      homeColumns,
      homeList: syncHomeList(homeColumns),
    };
  }

  case OrdersActionTypes.SET_NEW_ORDERS_COUNT:
    return {
      ...state,
      newOrdersCount: action.payload,
    };

  default:
    return state;
  }
};

export default ordersReducer;
